import { TInputSelectOption } from '@sureapp/wl-toggle-common/dist/components/InputSelect';

import {
  UmbiModalContent,
  UmpdModalContent,
} from '@@/auto/components/__regionalComponents/Georgia';
import { TConfig, TConfigInput, TConfigParams } from '@@/auto/interfaces';

import { AUTO_DISCOUNTS } from '../../autoDiscounts';
import { featureFlag } from '../../featureFlags';

function ToggleGAConfig({
  isFeatureEnabled,
}: TConfigParams): DeepPartial<TConfigInput> {
  return isFeatureEnabled(featureFlag.IS_GA_EXPANSION_ENABLED)
    ? {
        autoDiscounts: [
          AUTO_DISCOUNTS.affinity,
          AUTO_DISCOUNTS.defensiveDriverAll,
          AUTO_DISCOUNTS.safeDriving,
          AUTO_DISCOUNTS.multiCar,
          AUTO_DISCOUNTS.multiPolicy,
          AUTO_DISCOUNTS.homeowner,
          AUTO_DISCOUNTS.goodStudent25,
          AUTO_DISCOUNTS.distantStudent25,
          AUTO_DISCOUNTS.teenDriver,
        ],
        sanitizeCoverageRegexRecord: () => ({
          uninsuredMotoristPropertyDamage: /- Reduced By|- Added On/g,
          uninsuredMotoristBodilyInjury: /- Reduced By|- Added On/g,
        }),
        sortCoverages: () => ({
          uninsuredMotoristBodilyInjury: (
            a: TInputSelectOption,
            b: TInputSelectOption
          ) => {
            const regex = /- Reduced By|- Added On/g;
            const aValue = a.value.toString().replace(regex, '').trim();
            const bValue = b.value.toString().replace(regex, '').trim();
            /*
             * If the values are the same, then
             * prioritize "Reduced By" over "Added On"
             */
            if (aValue === bValue) {
              return (
                a.value
                  .match(regex)!
                  .toString()
                  .localeCompare(b.value.match(regex)?.toString() || '') * -1
              );
            }
            return aValue
              .toString()
              .localeCompare(bValue, undefined, { numeric: true });
          },
        }),
        coverage: (coverage: TConfig['coverage']) => ({
          ...coverage,
          uninsuredMotoristPropertyDamage: {
            description: `Your bumper sticker might say 'don't hit me if you're uninsured' but it isn't actually protecting you against that. Don't worry, this coverage can help.`,
            modalContent: <UmpdModalContent />,
            title: 'Uninsured Motorist Property Damage',
          },
          uninsuredMotoristBodilyInjury: {
            title: 'Uninsured Motorist Bodily Injury',
            description:
              "That pain in your neck is not just from the accident - it's also from finding out that the person who hit you has no insurance. This coverage can help.",
            modalContent: <UmbiModalContent />,
            textAlertContent:
              'We’ve decreased your Uninsured motorist bodily injury and Underinsured motorist bodily injury limits to match the changes you’ve made to your Bodily injury coverage.',
          },
        }),
        defensiveDriver: () => ({
          getSubjectText: null,
          label:
            'Has this person taken a defensive driving course within the last 3 years?',
          explanation:
            'Approved defensive driving courses help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
        }),
        visibleCoverages: (visibleCoverages: TConfig['visibleCoverages']) => {
          return {
            ...visibleCoverages,
            protectionCoverages: [
              ...visibleCoverages.protectionCoverages.filter(
                ({ key }) =>
                  ![
                    'pet_passenger',
                    'underinsured_motorist_bodily_injury_limit',
                    'uninsured_motorist_property_damage_limit',
                  ].includes(key)
              ),
              {
                label: 'Uninsured Motorist Property Damage',
                key: 'uninsured_motorist_property_damage_limit',
              },
              visibleCoverages.protectionCoverages.find(
                ({ key }) => key === 'pet_passenger'
              ),
            ],
            vehicleCoverages: [
              ...visibleCoverages.vehicleCoverages.filter(
                ({ key }) => key !== 'uninsured_motorist_property_damage_limit'
              ),
            ],
          };
        },
        initialCoverageValues: {
          gap: 'false',
          temporary_ride: 'null',
          side_hustle: 'false',
          new_car_replacement: 'false',
          oem_parts: 'false',
          towing_road_services: 'false',
        },
        vehicleSideEffectMessages: (
          vehicleSideEffectMessages: TConfig['vehicleSideEffectMessages']
        ) => ({
          ...vehicleSideEffectMessages,
          new_car_replacement: `This coverage is only available if Comprehensive & Collision is also selected and the model year is two (2) years old or less.`,
          gap:
            'This coverage is only available if Comprehensive and Collision are also selected, the vehicle is financed or leased, and the model year is four (4) years old or less.',
        }),
        coverageMarkOverrides: () => ({
          uninsured_motorist_property_damage_limit: {
            value: 25000,
            label: 'On',
          },
        }),
      }
    : {};
}
export default ToggleGAConfig;
